import { z } from "zod";
import { LESSON_THEME_COLORS } from './course';

const TypographyScaleSchema = z.object({
  size: z.string(),
  weight: z.number(),
  lineHeight: z.string(),
});

const ResponsiveValueSchema = z.object({
  desktop: z.string(),
  mobile: z.string(),
});

const SidebarLayoutSchema = z.object({
  width: z.string(),
  minWidth: z.string(),
  maxWidth: z.string(),
});

const NavigationTokensSchema = z.object({
  borderColor: z.string(),
  titleColor: z.string(),
  emptyColor: z.string(),
  itemColor: z.string(),
  itemActiveColor: z.string(),
  itemActiveBackground: z.string(),
  itemLockedOpacity: z.number(),
  titleFontSize: z.string(),
  itemFontSize: z.string(),
  titlePadding: z.string(),
  listPadding: z.string(),
  itemPadding: z.string(),
  itemGap: z.string(),
  itemBorderRadius: z.string(),
  lockIconSize: z.string(),
});

// The design-system token set covers only reusable primitives (color, type,
// spacing, radius, shadow). Per-component layout values live as local constants
// in each component's renderer/style file.

const UiTokensSchema = z.object({
  // Fixed interactive blue for UI chrome and selection states (NA-242). This is
  // deliberately OUTSIDE the account theming structure: the account theme mapper
  // never writes the ui group, and changing the default account accent must not
  // move these elements. Do not point account or lesson theming at it.
  fixedAccent: z.string(),
  fixedAccentHover: z.string(),
  selectionBorder: z.string(),
  focusRing: z.string(),
  focusRingOffset: z.string(),
  workflowDraft: z.string(),
  workflowReview: z.string(),
  workflowApproved: z.string(),
  workflowPublished: z.string(),
  workflowArchived: z.string(),
  errorBorder: z.string(),
  errorBackground: z.string(),
  errorText: z.string(),
  overlayMarker: z.string(),
  overlayMarkerBackground: z.string(),
  accountAssignmentAction: z.string(),
  accountAssignmentCancelText: z.string(),
  accountAssignmentOverlay: z.string(),
  transitionDefault: z.string(),
  transitionSlow: z.string(),
});

const ButtonsTokensSchema = z.object({
  action: z.object({
    bg: z.string(),
    hover: z.string(),
    disabledBg: z.string(),
    disabledFg: z.string(),
  }),
});

const ChoiceStateTokensSchema = z.object({
  border: z.string(),
  bg: z.string(),
});

const ChoiceTokensSchema = z.object({
  hover: ChoiceStateTokensSchema,
  drag: ChoiceStateTokensSchema,
  wrong: ChoiceStateTokensSchema,
  correct: ChoiceStateTokensSchema,
});

const SemanticTokensSchema = z.object({
  success: z.string(),
  successBg: z.string(),
  error: z.string(),
  errorBg: z.string(),
});

const WcsTokensSchema = z.object({
  green: z.string(),
  yellow: z.string(),
  yellowLegend: z.string(),
  orange: z.string(),
  red: z.string(),
});

const BarTokensSchema = z.object({
  myOrg: z.string(),
  global: z.string(),
});

const BannerTokensSchema = z.object({
  info: z.object({
    background: z.string(),
    border: z.string(),
  }),
});

const LikertTokensSchema = z.object({
  negative: z.object({
    strong: z.string(),
    medium: z.string(),
    light: z.string(),
  }),
  neutral: z.string(),
  positive: z.object({
    light: z.string(),
    medium: z.string(),
    strong: z.string(),
  }),
});

const BadgeTokensSchema = z.object({
  rosette: z.string(),
  star: z.string(),
  ribbon: z.string(),
  ribbonShadow: z.string(),
  size: z.string(),
});

// Completion-state tokens — sidebar top border, lesson-page banner bg,
// lesson-row check tile, "Completed" subtitle.
const CompletionTokensSchema = z.object({
  accent: z.string(),
  label: z.string(),
});

// Per-slot lesson color palette. Positional mapping mirrors LessonColors in
// course.ts (blue→color1, magenta→color2, orange→color3, navy→color4).
// .default({}) seeded from LESSON_THEME_COLORS so parseTheme() accepts older
// serialized themes that predate this field.
const LessonColorsSchema = z.object({
  color1: z.string(),
  color2: z.string(),
  color3: z.string(),
  color4: z.string(),
});


const TypographySizeMapSchema = z.object({
  xs: z.string(),
  sm: z.string(),
  base: z.string(),
  ui: z.string(),
  md: z.string(),
  lg: z.string(),
  h3: z.string(),
  h2: z.string(),
  h1: z.string(),
  lessonTitle: z.string(),
});

const TypographyWeightMapSchema = z.object({
  light: z.number(),
  regular: z.number(),
  medium: z.number(),
  semibold: z.number(),
  bold: z.number(),
});

const TypographyLineHeightMapSchema = z.object({
  normal: z.string(),
  lesson: z.string(),
  lessonMobile: z.string(),
  scale: z.string(),
  uiSoft: z.string(),
  tight: z.string(),
});

export const ThemeTokensSchema = z.object({
  brand: z.object({
    primary: z.string(),
    primaryHover: z.string(),
    accent: z.string().optional(),
    accentHover: z.string().optional(),
  }),

  backgrounds: z.object({
    app: z.string(),
    page: z.string(),
    courseShell: z.string(),
    surface: z.string(),
    surfaceAlt: z.string(),
    lessonSectionDefault: z.string(),
    lessonSectionTinted: z.string(),
    inverse: z.string(),
    // Design-system aligned
    card: z.string(),
    cardAlt: z.string(),
    resultsPanel: z.string(),
    sidebar: z.string(),
    sidebarLogo: z.string(),
    dot: z.string(),
  }),

  text: z.object({
    primary: z.string(),
    muted: z.string(),
    subtle: z.string(),
    callout: z.string(),
    onBrand: z.string(),
    // Computed from brand.accent (NA-294) rather than static, since accent is
    // account-configurable and can be light or dark — see getReadableTextColor.
    onAccent: z.string(),
    onInverse: z.string(),
    // Additive
    link: z.string(),
  }),

  borders: z.object({
    default: z.string(),
    inverse: z.string(),
    interactive: z.string(),
    dropZone: z.string(),
  }),

  shadow: z.object({
    sm: z.string(),
    md: z.string(),
    lg: z.string(),
    xl: z.string(),
    itemPill: z.string(),
    itemPillDrag: z.string(),
  }),

  radius: z.object({
    xs: z.string(),
    sm: z.string(),
    md: z.string(),
    lg: z.string(),
    xl: z.string(),
    "2xl": z.string(),
    full: z.string(),
  }),

  // Semantic spacing scale, mirrors --space-* in colors_and_type.css.
  // lgPlus is the 20px mid-step between lg (16) and xl (24).
  spacing: z.object({
    none:   z.string(),
    xs:     z.string(),
    sm:     z.string(),
    md:     z.string(),
    lg:     z.string(),
    lgPlus: z.string(),
    xl:     z.string(),
    "2xl":  z.string(),
    "3xl":  z.string(),
    "4xl":  z.string(),
    "5xl":  z.string(),
    "6xl":  z.string(),
    "7xl":  z.string(),
  }),

  layout: z.object({
    mobileBreakpoint: z.number(),
    smallMobileBreakpoint: z.number(),
    contentMaxWidth: z.string(),
    sectionPaddingY: ResponsiveValueSchema,
    sectionPaddingX: ResponsiveValueSchema,
    sidebar: SidebarLayoutSchema,
  }),

  navigation: NavigationTokensSchema,

  typography: z.object({
    fontFamily: z.string(),
    h1: TypographyScaleSchema,
    h2: TypographyScaleSchema,
    h3: TypographyScaleSchema,
    h4: TypographyScaleSchema,
    body: TypographyScaleSchema,
    display: TypographyScaleSchema,
    callout: TypographyScaleSchema,
    // Additive — design-system aligned
    lessonTitle: TypographyScaleSchema,
    calloutSm: TypographyScaleSchema,
    calloutMd: TypographyScaleSchema,
    calloutLg: TypographyScaleSchema,
    sizes: TypographySizeMapSchema,
    weights: TypographyWeightMapSchema,
    lineHeights: TypographyLineHeightMapSchema,
    mobile: z.object({
      h1: TypographyScaleSchema,
      h2: TypographyScaleSchema,
      h3: TypographyScaleSchema,
      h4: TypographyScaleSchema,
      body: TypographyScaleSchema,
      display: TypographyScaleSchema,
      callout: TypographyScaleSchema,
      // Additive
      lessonTitle: TypographyScaleSchema,
      calloutSm: TypographyScaleSchema,
      calloutMd: TypographyScaleSchema,
      calloutLg: TypographyScaleSchema,
    }),
  }),

  ui: UiTokensSchema,

  // New design-system top-level namespaces (additive)
  buttons: ButtonsTokensSchema,
  choice: ChoiceTokensSchema,
  semantic: SemanticTokensSchema,
  wcs: WcsTokensSchema,
  bar: BarTokensSchema,
  banner: BannerTokensSchema,
  likert: LikertTokensSchema,
  badge: BadgeTokensSchema,
  completion: CompletionTokensSchema,

  // Brand asset URLs resolved at runtime (account → base → default).
  // Lenient string (not .url()) to allow signed/relative URLs.
  assets: z.object({
    primaryLogoUrl: z.string().optional(),
  }).default({}),

  // Per-slot lesson color palette — account overrides seeded from LESSON_THEME_COLORS defaults.
  // .default({...}) lets parseTheme() accept older full-theme JSON lacking this field.
  lessonColors: LessonColorsSchema.default({ ...LESSON_THEME_COLORS }),
});

export type ThemeTokens = z.infer<typeof ThemeTokensSchema>;

export type BrandTokens = ThemeTokens["brand"];
export type BackgroundTokens = ThemeTokens["backgrounds"];
export type TextTokens = ThemeTokens["text"];
export type BorderTokens = ThemeTokens["borders"];
export type ShadowTokens = ThemeTokens["shadow"];
export type RadiusTokens = ThemeTokens["radius"];
export type SpacingTokens = ThemeTokens["spacing"];
export type LayoutTokens = ThemeTokens["layout"];
export type NavigationTokens = ThemeTokens["navigation"];
export type TypographyTokens = ThemeTokens["typography"];
export type UiTokens = ThemeTokens["ui"];
export type ButtonsTokens = ThemeTokens["buttons"];
export type ChoiceTokens = ThemeTokens["choice"];
export type SemanticTokens = ThemeTokens["semantic"];
export type WcsTokens = ThemeTokens["wcs"];
export type BarTokens = ThemeTokens["bar"];
export type LikertTokens = ThemeTokens["likert"];
export type BadgeTokens = ThemeTokens["badge"];
export type CompletionTokens = ThemeTokens["completion"];
export type AssetsTokens = ThemeTokens["assets"];
export type LessonColorsTokens = ThemeTokens["lessonColors"];

/** Default theme — values mirrored from the design-system bundle. */
export const defaultThemeTokens: ThemeTokens = {
  brand: {
    primary: "#40405D",
    primaryHover: "#31314A",
    accent: "#5B8EFF",
    accentHover: "#4E80E6",
  },

  backgrounds: {
    app: "#FFFFFF",
    page: "#FFFFFF",
    courseShell: "#FFFFFF",
    surface: "#F5F5F5",
    surfaceAlt: "#EDEDED",
    lessonSectionDefault: "#FFFFFF",
    lessonSectionTinted: "#EDEDED",
    inverse: "#171932",
    card: "#FCFCFC",
    cardAlt: "#F1F1F1",
    resultsPanel: "#F1F1F1",
    sidebar: "#272729",
    sidebarLogo: "#1D1D1F",
    dot: "#28282A",
  },

  text: {
    primary: "#40405D",
    muted: "#666A80",
    subtle: "#8A8FA6",
    callout: "#40405D",
    onBrand: "#FFFFFF",
    onAccent: "#FFFFFF",
    onInverse: "#FFFFFF",
    link: "#4183C4",
  },

  borders: {
    default: "#CACACA",
    inverse: "#2D3154",         // editor sidebar — outside design-system scope
    interactive: "#A0A0A4",
    dropZone: "#263238",        // Single Choice empty drop zone (dashed)
  },

  // Format matches design-system bundle exactly (`0px` not `0`; no spaces in rgba).
  shadow: {
    sm: "0px 1px 2px rgba(0,0,0,0.08)",
    md: "0px 1px 3px rgba(0,0,0,0.10)",
    lg: "0px 4px 4px rgba(0,0,0,0.25)",
    xl: "0px 10px 26px rgba(0,0,0,0.28)",
    itemPill: "0px 3px 3px rgba(0,0,0,0.25)",
    itemPillDrag: "0px 5px 5px rgba(0,0,0,0.25)",
  },

  radius: {
    xs: "4px",
    sm: "6px",
    md: "8px",
    lg: "12px",
    xl: "15px",
    "2xl": "16px",
    full: "999px",
  },

  spacing: {
    none:   "0px",
    xs:     "4px",
    sm:     "8px",
    md:     "12px",
    lg:     "16px",
    lgPlus: "20px",
    xl:     "24px",
    "2xl":  "32px",
    "3xl":  "40px",
    "4xl":  "48px",
    "5xl":  "64px",
    "6xl":  "80px",
    "7xl":  "100px",
  },

  layout: {
    mobileBreakpoint: 768,
    // NA-205: narrow-phone tier (≤576px) that layers extra tweaks under the 768px mobile branch.
    smallMobileBreakpoint: 576,
    contentMaxWidth: "1390px",
    sectionPaddingY: {
      desktop: "100px",
      mobile: "100px",
    },
    sectionPaddingX: {
      desktop: "64px",
      mobile: "24px",
    },
    sidebar: {
      width: "23%",
      minWidth: "340px",
      maxWidth: "400px",
    },
  },

  navigation: {
    borderColor: "#2D3154",
    titleColor: "#FFFFFF",
    emptyColor: "rgba(255, 255, 255, 0.72)",
    itemColor: "rgba(255, 255, 255, 0.76)",
    itemActiveColor: "#FFFFFF",
    itemActiveBackground: "rgba(91, 142, 255, 0.32)",
    itemLockedOpacity: 0.5,
    titleFontSize: "16px",
    itemFontSize: "14px",
    titlePadding: "20px 16px",
    listPadding: "12px 8px",
    itemPadding: "12px",
    itemGap: "12px",
    itemBorderRadius: "8px",
    lockIconSize: "12px",
  },

  typography: {
    // Source Sans 3 ships as a variable TTF in packages/shared-types/src/assets/fonts/
    // and is loaded via packages/shared-types/src/fonts.css (imported by both apps).
    fontFamily: `"Source Sans 3", "Source Sans Pro", system-ui, sans-serif`,
    h1: { size: "50px", weight: 700, lineHeight: "normal" },
    h2: { size: "40px", weight: 700, lineHeight: "normal" },
    h3: { size: "30px", weight: 700, lineHeight: "normal" },
    h4: { size: "20px", weight: 700, lineHeight: "normal" },
    body: { size: "20px", weight: 400, lineHeight: "normal" },
    display: { size: "60px", weight: 300, lineHeight: "normal" },
    callout: { size: "60px", weight: 300, lineHeight: "normal" },
    lessonTitle: { size: "60px", weight: 700, lineHeight: "65px" },
    calloutSm: { size: "30px", weight: 300, lineHeight: "normal" },
    calloutMd: { size: "40px", weight: 300, lineHeight: "normal" },
    calloutLg: { size: "50px", weight: 300, lineHeight: "normal" },
    sizes: {
      xs: "12px",
      sm: "14px",
      base: "16px",
      ui: "18px",
      md: "20px",
      lg: "24px",
      h3: "30px",
      h2: "40px",
      h1: "50px",
      lessonTitle: "60px",
    },
    weights: {
      light: 300,
      regular: 400,
      medium: 500,
      semibold: 600,
      bold: 700,
    },
    lineHeights: {
      normal: "normal",
      lesson: "65px",
      lessonMobile: "48px",
      scale: "1.35",
      uiSoft: "1.4",
      tight: "1.2",
    },
    mobile: {
      h1: { size: "38px", weight: 700, lineHeight: "normal" },
      h2: { size: "30px", weight: 700, lineHeight: "normal" },
      h3: { size: "24px", weight: 700, lineHeight: "normal" },
      h4: { size: "16px", weight: 700, lineHeight: "normal" },
      body: { size: "16px", weight: 400, lineHeight: "normal" },
      display: { size: "40px", weight: 300, lineHeight: "normal" },
      callout: { size: "40px", weight: 300, lineHeight: "normal" },
      lessonTitle: { size: "44px", weight: 700, lineHeight: "48px" },
      calloutSm: { size: "24px", weight: 300, lineHeight: "normal" },
      calloutMd: { size: "32px", weight: 300, lineHeight: "normal" },
      calloutLg: { size: "40px", weight: 300, lineHeight: "normal" },
    },
  },

  ui: {
    fixedAccent: "#5B8EFF",
    fixedAccentHover: "#4E80E6",
    selectionBorder: "#2563EB",
    focusRing: "#3B82F6",
    focusRingOffset: "#FFFFFF",
    workflowDraft: "#F59E0B",
    workflowReview: "#8B5CF6",
    workflowApproved: "#10B981",
    workflowPublished: "#06B6D4",
    workflowArchived: "#6B7280",
    errorBorder: "#EF4444",
    errorBackground: "#FEF2F2",
    errorText: "#B91C1C",
    overlayMarker: "#F97316",
    overlayMarkerBackground: "#FFF7ED",
    accountAssignmentAction: "#2A6DD9",
    accountAssignmentCancelText: "#444444",
    accountAssignmentOverlay: "rgba(0, 0, 0, 0.35)",
    transitionDefault: "150ms ease-in-out",
    transitionSlow: "300ms ease-in-out",
  },

  buttons: {
    action: {
      bg: "#23242B",
      hover: "#33353F",
      disabledBg: "#D9D9D9",
      disabledFg: "#818181",
    },
  },

  choice: {
    hover:   { border: "#5B8EFF", bg: "#E1EBFF" },
    drag:    { border: "#5B8EFF", bg: "#E1EBFF" },
    wrong:   { border: "#DC2626", bg: "#F0DEDE" },
    correct: { border: "#1BA13F", bg: "#DBF1E5" },
  },

  semantic: {
    success:   "#1BA13F",
    successBg: "#DBF1E5",
    error:     "#DC2626",
    errorBg:   "#F0DEDE",
  },

  wcs: {
    green:        "#6FBE55",
    yellow:       "#FFED0F",
    yellowLegend: "#E2A407", // darker variant for legend swatches — pure yellow illegible at small sizes
    orange:       "#F9861C",
    red:          "#ED1E63",
  },

  bar: {
    myOrg:  "#166FD7",
    global: "#B7CEFF",
  },

  banner: {
    info: {
      background: "#EEF3FF",
      border:     "#166FD7",
    },
  },

  likert: {
    negative: {
      strong: "#F5853F",
      medium: "#FB923C",
      light:  "#FDBA74",
    },
    neutral: "#A0A0A4",
    positive: {
      light:  "#93C5FD",
      medium: "#60A5FA",
      strong: "#166FD7",
    },
  },

  badge: {
    rosette:      "#F7C906",
    star:         "#FFFFFF",
    ribbon:       "#1C75BC",
    ribbonShadow: "#0A63A0",
    size:         "198px",
  },

  completion: {
    accent: "#1BA13F",
    label:  "#2DA85B",
  },

  assets: {},

  lessonColors: { ...LESSON_THEME_COLORS },
};

/**
 * Utility type for partial theme overrides
 */
export type DeepPartial<T> = {
  [P in keyof T]?: T[P] extends object ? DeepPartial<T[P]> : T[P];
};

